import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useSearchParams } from "react-router-dom";
import { Eye,
  Image as ImageIcon,
  Search } from "lucide-react";
import { chatService } from "@b2b/services";
import { sellerService } from "@b2b/services";
import { useAuth } from "../../auth/Authprovider";
import { dispatchChatUnreadRefresh } from "../../chat/chatUnreadEvents";
import { useChatSocket } from "../../hooks/useChatSocket";
import { BizcoverIndiaFigmaHeader } from "../../components/marketing/BizcoverIndiaFigmaHeader";
import { Button,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Loader,
} from "@b2b/ui-components";


type ConversationListItem = any;
type ChatMessage = any;

const formatName = (p: any) =>
  `${p?.firstName || ""} ${p?.lastName || ""}`.trim() || p?.email || "User";

function formatMessageTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function sameLocalCalendarDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** "Today" / "Yesterday" / e.g. "25 March 2026" for chat day separators. */
function formatChatDayLabel(iso: string) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (sameLocalCalendarDay(d, now)) return "Today";
    if (sameLocalCalendarDay(d, yesterday)) return "Yesterday";
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function localDayStartMs(iso: string): number | null {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  } catch {
    return null;
  }
}

/** Sidebar preview: image-only messages are stored as "[attachment]" server-side — show icon + label like WhatsApp. */
function isPhotoOnlyConversationPreview(lastMessage: any): boolean {
  if (!lastMessage) return false;
  const text = String(lastMessage.message ?? "").trim();
  if (text === "[attachment]") return true;
  const atts = lastMessage.attachments;
  return Array.isArray(atts) && atts.length > 0 && !text;
}

/** Single ✓ = delivered, double ✓ = read; blue ticks on orange bubble. */
function OutgoingMessageTicks({ isRead }: { isRead: boolean }) {
  return (
    <span className="inline-flex items-center ml-1 select-none" aria-hidden title={isRead ? "Read" : "Sent"}>
      {isRead ? (
        <span className="text-blue-300 font-bold">✓✓</span>
      ) : (
        <span className="text-gray-300">✓</span>
      )}
    </span>
  );
}

export default function ChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const previousUrl = location.state?.from;

  const { userRole, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  /** Router recreates `setSearchParams` whenever `location.search` changes — never use it in effect deps. */
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;
  const sellerId = searchParams.get("sellerId");
  const conversationIdFromQuery = searchParams.get("conversationId");

  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [activeCounterpart, setActiveCounterpart] = useState<any | null>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [conversationSearch, setConversationSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  /** Data URL from FileReader — works reliably in <img> (blob URLs can fail under Strict Mode / some policies). */
  const [displayPreviewSrc, setDisplayPreviewSrc] = useState<string | null>(null);
  const [fullImagePreviewOpen, setFullImagePreviewOpen] = useState(false);
  /** Invalidates in-flight FileReader results when selection changes or preview is cleared. */
  const previewLoadSeqRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const shouldAutoScrollRef = useRef(true);
  const forceAutoScrollNextRef = useRef(false);
  const loadingOlderRef = useRef(false);
  /** When true, skip scroll-to-bottom effect after messages change (e.g. prepending older pages). */
  const suppressAutoScrollForPrependRef = useRef(false);
  /** Scroll metrics captured before prepending older messages; applied in useLayoutEffect after commit. */
  const pendingPrependScrollRef = useRef<{ prevScrollHeight: number; prevScrollTop: number } | null>(
    null
  );
  /** Top-of-list sentinel for infinite scroll (load older when user scrolls up). */
  const loadOlderSentinelRef = useRef<HTMLDivElement | null>(null);
  const nextCursorRef = useRef<string | null>(null);
  const messagesLoadingRef = useRef(false);
  /** Distinguish real /chat?sellerId=… or ?conversationId=… navigations from list-only refetches (same conv). */
  const lastThreadNavKeyRef = useRef<string | null>(null);
  /**
   * Until the first pin after opening a thread, scrollTop is often 0. handleChatScroll would set
   * shouldAutoScrollRef false ("user not near latest") and infinite-scroll could fetch older pages — wrong.
   * Block those until useLayoutEffect has anchored the viewport on the newest messages.
   */
  const pendingInitialPinToLatestRef = useRef(false);
  /** True while we intentionally cleared messages to switch threads (avoid treating transient [] as "empty chat"). */
  const isSwitchingThreadRef = useRef(false);
  /**
   * Monotonic id for each thread load started from activate-conversation. Stale HTTP completions must not
   * leave messagesLoading stuck true (which blocks scroll-to-latest) nor clear isSwitching for the wrong load.
   */
  const loadThreadSeqRef = useRef(0);
  /** Conversation id for the message list currently committed from loadInitialThread (source-of-truth for open pin). */
  const loadedMessagesConversationIdRef = useRef<string | null>(null);

  const [onlineByUserId, setOnlineByUserId] = useState<Record<string, boolean>>({});
  /** True after first `presence:snapshot` while socket is connected (so "offline" is meaningful). */
  const [presenceReady, setPresenceReady] = useState(false);

  const [isCompactChat, setIsCompactChat] = useState(
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );

  /** Must be defined before scroll layout effects: thread column visibility drives scroll container height (0 when hidden on mobile). */
  const showThreadOnMobile =
    isCompactChat && (!!conversationIdFromQuery || !!sellerId);
  const showListPanel = !isCompactChat || !showThreadOnMobile;
  const showThreadPanel = !isCompactChat || showThreadOnMobile;

  const activeConversationIdRef = useRef<string | null>(null);
  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    nextCursorRef.current = nextCursor;
  }, [nextCursor]);

  useEffect(() => {
    messagesLoadingRef.current = messagesLoading;
  }, [messagesLoading]);

  const attachmentUrlsRef = useRef<Record<string, string>>({});
  useEffect(() => {
    attachmentUrlsRef.current = attachmentUrls;
  }, [attachmentUrls]);

  const conversationsRef = useRef<ConversationListItem[]>([]);
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  const clearUnreadLocal = useCallback((conversationId: string) => {
    setConversations((prev) =>
      prev.map((c) =>
        String(c.id) === String(conversationId) ? { ...c, unreadCount: 0 } : c
      )
    );
  }, []);

  /** Merge GET /conversations into local state; zero unread for the open thread so UI matches mark-read. */
  const mergeServerConversationItemsIntoState = useCallback((items: ConversationListItem[]) => {
    setConversations((prev) => {
      const serverIds = new Set(items.map((c: ConversationListItem) => String(c.id)));
      const onlyLocal = prev.filter((c) => !serverIds.has(String(c.id)));
      let merged = [...onlyLocal, ...items] as ConversationListItem[];
      const activeId = activeConversationIdRef.current;
      if (activeId) {
        merged = merged.map((c) =>
          String(c.id) === String(activeId) ? { ...c, unreadCount: 0 } : c
        );
      }
      return merged;
    });
  }, []);

  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await chatService.listConversations({ page: 1, limit: 50 });
      const items = data.items || [];
      // Keep threads merged from createOrGet until the server list includes them (GET can finish before POST upsert).
      mergeServerConversationItemsIntoState(items);
      dispatchChatUnreadRefresh();
    } finally {
      setLoading(false);
    }
  }, [mergeServerConversationItemsIntoState]);

  /**
   * `conversations` is replaced on every preview/unread/socket update. The thread-activation effect only
   * needs to re-run when the set of conversation ids can affect URL↔thread resolution — not on each message.
   */
  const conversationIdsFingerprint = useMemo(
    () =>
      conversations
        .map((c: ConversationListItem) => String(c.id))
        .filter(Boolean)
        .sort()
        .join("|"),
    [conversations]
  );

  const filteredConversations = useMemo(() => {
    const query = conversationSearch.trim().toLowerCase();
    if (!query) return conversations;
    return conversations.filter((c: ConversationListItem) => {
      const name = formatName(c?.counterpart).toLowerCase();
      const email = String(c?.counterpart?.email || "").toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [conversations, conversationSearch]);

  /**
   * Pin the thread viewport to the newest messages. Uses direct scrollTop for "auto" so CSS
   * scroll-behavior on the container cannot block or animate an incomplete scroll.
   */
  const scrollToLatestMessage = useCallback((behavior: ScrollBehavior = "auto") => {
    const el = chatContainerRef.current;
    if (!el) return;
    const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
    if (behavior === "smooth") {
      el.scrollTo({ top: maxTop, behavior: "smooth" });
    } else {
      el.scrollTop = maxTop;
    }
  }, []);

  /**
   * One follow-up frame after async paths (e.g. fetch returned before panel laid out).
   * Pass forConversationId so rapid thread switches do not apply a stale follow-up scroll.
   */
  const schedulePinToLatest = useCallback(
    (behavior: ScrollBehavior = "auto", forConversationId?: string | null) => {
      scrollToLatestMessage(behavior);
      requestAnimationFrame(() => {
        if (
          forConversationId != null &&
          activeConversationIdRef.current !== forConversationId
        ) {
          return;
        }
        scrollToLatestMessage(behavior);
      });
    },
    [scrollToLatestMessage]
  );

  const socket = useChatSocket(
    {
      onMessageNew: async (payload) => {
        const msg = payload?.message;
        if (!msg?.conversationId) return;

        const currentConvoId = activeConversationIdRef.current;
        const isActiveConversation = !!currentConvoId && msg.conversationId === currentConvoId;
        const conversationForMsg = conversationsRef.current.find((c) => c.id === msg.conversationId);
        const counterpartId = conversationForMsg?.counterpart?.id;
        const isIncomingForConversation = counterpartId ? counterpartId === msg.senderId : msg.senderId !== "me";

        setConversations((prev) => {
          const idx = prev.findIndex((c) => String(c.id) === String(msg.conversationId));
          if (idx === -1) return prev;
          const current = prev[idx];
          const unreadCount =
            isActiveConversation || !isIncomingForConversation
              ? 0
              : Math.max(0, Number(current.unreadCount || 0) + 1);
          const updated = {
            ...current,
            unreadCount,
            lastMessage: msg,
          };
          if (idx === 0) return [updated, ...prev.slice(1)];
          return [updated, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
        });

        if (!isActiveConversation && isIncomingForConversation) {
          dispatchChatUnreadRefresh();
        }

        if (!currentConvoId || msg.conversationId !== currentConvoId) return;

        if (msg.attachments && msg.attachments.length > 0) {
          const currentUrls = attachmentUrlsRef.current;
          const missingKeys = msg.attachments.filter((k: string) => !!k && !currentUrls[k]);
          if (missingKeys.length > 0) {
            const urlEntries = await Promise.all(
              missingKeys.map(async (k: string) => {
                try {
                  const res = await chatService.getAttachmentUrl(k);
                  return [k, res.url] as const;
                } catch (err: any) {
                  console.error(`Failed to fetch URL for ${k}:`, err);
                  return [k, ""] as const;
                }
              })
            );
            setAttachmentUrls((prev) => {
              const next = { ...prev };
              for (const [k, url] of urlEntries) {
                if (url) next[k] = url;
              }
              return next;
            });
          }
        }

        setMessages((prev) => {
          const withoutOptimistic = prev.filter((m) => !String(m.id).startsWith("temp-"));
          if (withoutOptimistic.some((m) => m.id === msg.id)) return withoutOptimistic;
          return [msg, ...withoutOptimistic];
        });
        clearUnreadLocal(msg.conversationId);

        try {
          await chatService.markRead(msg.conversationId);
        } catch {
          /* ignore */
        }
      },
      onMessagesRead: ({ conversationId, readByUserId }) => {
        if (conversationId !== activeConversationIdRef.current) return;
        const counterpartId =
          conversationsRef.current.find((c) => c.id === conversationId)?.counterpart?.id ?? null;
        if (!counterpartId || readByUserId !== counterpartId) return;
        // 1:1 DM: my messages are those not sent by the counterpart.
        setMessages((prev) =>
          prev.map((m) =>
            m.senderId !== counterpartId
              ? { ...m, isRead: true, readAt: m.readAt ?? new Date().toISOString() }
              : m
          )
        );
      },
      onPresenceSnapshot: (snapshot) => {
        setOnlineByUserId(snapshot || {});
        setPresenceReady(true);
      },
      onPresenceUpdate: ({ userId: uid, online }) => {
        setOnlineByUserId((prev) => ({ ...prev, [uid]: online }));
      },
    },
    activeConversationIdRef
  );

  useEffect(() => {
    if (!socket.isConnected) setPresenceReady(false);
  }, [socket.isConnected]);

  /**
   * After messages commit: restore scroll when prepending history, otherwise jump to bottom before paint
   * so the infinite-scroll observer does not see scrollTop=0 on long threads (which would load older pages
   * and clear shouldAutoScrollRef before we anchor at the latest message).
   */
  useLayoutEffect(() => {
    const el = chatContainerRef.current;
    const pending = pendingPrependScrollRef.current;

    if (pending) {
      pendingPrependScrollRef.current = null;
      if (el) {
        const delta = el.scrollHeight - pending.prevScrollHeight;
        el.scrollTop = pending.prevScrollTop + delta;
      }
      suppressAutoScrollForPrependRef.current = false;
      return;
    }

    if (!messages.length) {
      // Only treat as a settled empty thread — not the brief [] between clearMessages and fetch start.
      if (
        !messagesLoading &&
        pendingInitialPinToLatestRef.current &&
        !isSwitchingThreadRef.current
      ) {
        pendingInitialPinToLatestRef.current = false;
      }
      return;
    }
    if (suppressAutoScrollForPrependRef.current) return;
    // Stale listMessages responses used to skip `finally`, leaving loading true and blocking this effect forever.
    // When we already have a first page and still need the open pin, run the pin even if loading is stale-true.
    if (
      messagesLoading &&
      !(pendingInitialPinToLatestRef.current && messages.length > 0)
    ) {
      return;
    }
    const mustPinAfterOpen = pendingInitialPinToLatestRef.current;
    const shouldScroll =
      mustPinAfterOpen ||
      shouldAutoScrollRef.current ||
      forceAutoScrollNextRef.current;
    if (!shouldScroll) return;
    const useSmooth = forceAutoScrollNextRef.current && !mustPinAfterOpen;
    scrollToLatestMessage(useSmooth ? "smooth" : "auto");
    forceAutoScrollNextRef.current = false;
    if (mustPinAfterOpen) {
      // Rapid conversation switches schedule multiple layout effects; rAF from an older thread
      // must not clear pending pin or fight the active thread. Prefer the conv id tied to the loaded list
      // so we do not mismatch React state vs URL for one frame.
      const pinSessionConversationId =
        loadedMessagesConversationIdRef.current ?? activeConversationId;
      const finishOpenPin = () => {
        if (activeConversationIdRef.current !== pinSessionConversationId) return;
        pendingInitialPinToLatestRef.current = false;
        shouldAutoScrollRef.current = true;
      };
      const isPinned = () => {
        const c = chatContainerRef.current;
        if (!c || c.clientHeight <= 0) return false;
        const dist = c.scrollHeight - c.scrollTop - c.clientHeight;
        return (
          dist <= 3 || c.scrollHeight <= c.clientHeight + 2
        );
      };
      // Same frame: already called scrollToLatestMessage above; avoid queueMicrotask (feels sluggish).
      if (isPinned()) {
        finishOpenPin();
      } else {
        let left = 8;
        const tick = () => {
          if (activeConversationIdRef.current !== pinSessionConversationId) return;
          scrollToLatestMessage("auto");
          if (isPinned() || left-- <= 0) finishOpenPin();
          else requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }
  }, [
    messages,
    messagesLoading,
    conversationIdFromQuery,
    sellerId,
    activeConversationId,
    showThreadPanel,
    scrollToLatestMessage,
  ]);

  // Mobile: thread mounts or gets height after conversationId appears; content/urls can resize the column.
  useEffect(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    const maybePin = () => {
      if (suppressAutoScrollForPrependRef.current || loadingOlderRef.current) return;
      if (pendingInitialPinToLatestRef.current) {
        scrollToLatestMessage("auto");
        return;
      }
      if (!shouldAutoScrollRef.current && !forceAutoScrollNextRef.current) return;
      scrollToLatestMessage("auto");
    };
    const ro = new ResizeObserver(() => maybePin());
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeConversationId, showThreadPanel, scrollToLatestMessage]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const onChange = () => setIsCompactChat(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Connect early so we receive presence:snapshot for the sidebar, not only after opening a thread.
  useEffect(() => {
    void socket.connect().catch(() => {});
  }, [socket.connect]);

  /** First page for a thread (newest chunk). */
  const loadInitialThread = useCallback(async (conversationId: string) => {
    setMessagesLoading(true);
    try {
      const data = await chatService.listMessages(conversationId, { limit: 30 });
      if (activeConversationIdRef.current !== conversationId) return;
      setMessages(data.items || []);
      setNextCursor(data.nextCursor ?? null);
    } finally {
      if (activeConversationIdRef.current === conversationId) {
        isSwitchingThreadRef.current = false;
        setMessagesLoading(false);
      }
    }
  }, []);

  /**
   * Next page when user scrolls up. API returns each page newest-first; our state is also newest-first.
   * Older pages must be appended: [...existing, ...olderPage] so that after `.reverse()` for display,
   * chronology is correct (older messages above). `[...olderPage, ...existing]` breaks Today/Yesterday separators.
   */
  const loadOlderMessagesPage = useCallback(async (conversationId: string, cursor: string) => {
    if (loadingOlderRef.current) return;
    loadingOlderRef.current = true;
    const container = chatContainerRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;
    const prevScrollTop = container?.scrollTop ?? 0;
    setLoadingOlder(true);
    suppressAutoScrollForPrependRef.current = true;
    shouldAutoScrollRef.current = false;
    try {
      const data = await chatService.listMessages(conversationId, { limit: 30, cursor });
      const items = data.items || [];
      pendingPrependScrollRef.current = { prevScrollHeight, prevScrollTop };
      setMessages((prev) => [...prev, ...items]);
      setNextCursor(data.nextCursor ?? null);
    } catch {
      suppressAutoScrollForPrependRef.current = false;
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, []);

  const handleChatScroll = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    if (pendingInitialPinToLatestRef.current || messagesLoadingRef.current) {
      return;
    }
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    const isNearLatest = distanceFromBottom < 100;
    shouldAutoScrollRef.current = isNearLatest;

    // Backup for load-older: same overflow rule as IntersectionObserver (short threads never get distBottom > 0).
    const overflows = container.scrollHeight > container.clientHeight + 8;
    if (overflows && distanceFromBottom < 120) return;
    if (container.scrollTop > 120) return;
    if (messagesLoadingRef.current) return;
    const cid = activeConversationIdRef.current;
    const cursor = nextCursorRef.current;
    if (!cid || !cursor || loadingOlderRef.current) return;
    void loadOlderMessagesPage(cid, cursor);
  }, [loadOlderMessagesPage]);

  /**
   * When the top sentinel intersects the scroll container, fetch older history.
   * Uses IntersectionObserver so we don’t depend on brittle scrollTop thresholds.
   */
  useEffect(() => {
    const root = chatContainerRef.current;
    const target = loadOlderSentinelRef.current;
    if (!root || !target || !activeConversationId || messagesLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (messagesLoadingRef.current || pendingInitialPinToLatestRef.current) return;
        const el = chatContainerRef.current;
        if (!el) return;
        const distBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        // If the thread is shorter than the viewport, distBottom stays ~0 even at scrollTop 0 — the top
        // sentinel is visible and we still must load older pages. Only apply “near latest” suppression
        // when content actually overflows (real scrollbar).
        const overflows = el.scrollHeight > el.clientHeight + 8;
        if (overflows && distBottom < 120) return;
        const cid = activeConversationIdRef.current;
        const cursor = nextCursorRef.current;
        if (!cid || !cursor || loadingOlderRef.current) return;
        void loadOlderMessagesPage(cid, cursor);
      },
      { root, rootMargin: "200px 0px 0px 0px", threshold: 0 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [activeConversationId, messagesLoading, messages.length, loadOlderMessagesPage, showThreadPanel]);

  const ensureAttachmentUrls = async (keys: string[]) => {
    const missing = keys.filter((k) => !!k && !attachmentUrls[k]);
    if (missing.length === 0) return;
    const entries = await Promise.all(
      missing.map(async (k) => {
        try {
          const res = await chatService.getAttachmentUrl(k);
          return [k, res.url] as const;
        } catch (error: any) {
          // Log error with context for debugging
          console.error(`Failed to fetch signed URL for attachment key "${k}":`, {
            error: error?.message || error,
            status: error?.response?.status,
            statusText: error?.response?.statusText,
          });
          return [k, ""] as const;
        }
      })
    );
    setAttachmentUrls((prev) => {
      const next = { ...prev };
      for (const [k, url] of entries) {
        if (url) next[k] = url;
      }
      return next;
    });
  };

  // Inbox list once per chat mount. Do not depend on `setSearchParams`: in React Router 6 its identity
  // changes whenever `location.search` changes, so the old deps re-ran after every conversation click and
  // `loadConversations` called `setLoading(true)` → full-screen loader (felt like a full page refresh).
  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  // Buyer deep-link from profile: ?sellerId=… (create/get conversation and set conversationId in URL).
  useEffect(() => {
    const role = String(userRole || "").toLowerCase();
    const canCreateFromSellerId = role === "buyer" || role === "buyer_seller";
    if (!canCreateFromSellerId || !sellerId) return;
    let cancelled = false;
    (async () => {
      const convo = await chatService.createOrGetConversation({ sellerId });
      if (cancelled) return;
      if (!convo?.id) {
        console.error("Chat: createOrGetConversation returned invalid conversation", convo);
        return;
      }
      // API returns buyer/seller joins, not `counterpart` — map so activation + header can resolve the seller.
      const counterpart = convo.seller ?? null;
      if (counterpart) {
        setConversations((prev) => {
          if (prev.some((c) => String(c.id) === String(convo.id))) return prev;
          const listItem: ConversationListItem = {
            id: convo.id,
            buyerId: convo.buyerId,
            sellerId: convo.sellerId,
            lastMessageAt: convo.lastMessageAt ?? null,
            unreadCount: Number(convo.buyerUnreadCount ?? 0),
            counterpart,
            lastMessage: null,
          };
          return [listItem, ...prev];
        });
      }
      setSearchParamsRef.current(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("conversationId", convo.id);
          next.delete("sellerId");
          return next;
        },
        { replace: true }
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [sellerId, userRole]);

  // When the conversation list arrives, fill in counterpart if it was missing on first open.
  useEffect(() => {
    if (!activeConversationId) return;
    const convo = conversations.find((c) => c.id === activeConversationId);
    if (convo?.counterpart) setActiveCounterpart(convo.counterpart);
  }, [activeConversationId, conversations]);

  // Fallback for deep-links: if counterpart is still missing, resolve seller profile directly by sellerId.
  useEffect(() => {
    if (!sellerId || activeCounterpart) return;
    let cancelled = false;
    (async () => {
      const listCounterpart = conversations.find((c) => c.counterpart?.id === sellerId)?.counterpart;
      if (listCounterpart) {
        setActiveCounterpart(listCounterpart);
        return;
      }
      try {
        const data = await sellerService.getSellerById(sellerId);
        if (cancelled) return;
        const seller = data?.seller;
        if (!seller?.id) return;
        setActiveCounterpart({
          id: seller.id,
          firstName: seller.firstName ?? null,
          lastName: seller.lastName ?? null,
          email: seller.email ?? "",
          profileImage: seller.profileImage ?? null,
          companyId: seller.companyId ?? "",
        });
      } catch {
        // Ignore fallback errors; header can still update from conversation list when available.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sellerId, activeCounterpart, conversations]);

  const clearImagePreview = useCallback(() => {
    previewLoadSeqRef.current += 1;
    setPreviewFile(null);
    setDisplayPreviewSrc(null);
    setFullImagePreviewOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  // Activate conversation from query or first item
  useEffect(() => {
    /** Buyer + ?sellerId= without conversationId yet: avoid conversations[0] / stale active id while createOrGet runs. */
    const role = String(userRole || "").toLowerCase();
    const pendingSellerTarget =
      (role === "buyer" || role === "buyer_seller") &&
      !!sellerId &&
      !conversationIdFromQuery;

    let nextId: string | null = conversationIdFromQuery || null;

    if (pendingSellerTarget) {
      if (!nextId) {
        nextId =
          conversations.find((c) => c.counterpart?.id === sellerId)?.id ?? null;
      }
    } else {
      if (!nextId) nextId = activeConversationId;
      if (!nextId && conversations.length > 0) nextId = conversations[0].id;
    }

    if (!nextId || nextId === "undefined" || nextId === "null") return;

    /** Stable key for “user navigated to this thread via URL” vs in-place list updates. */
    const navKey = `${conversationIdFromQuery ?? ""}|${sellerId ?? ""}|${nextId}`;

    // Same thread already selected (e.g. “Send message” from profile) — still jump to latest once per navigation.
    if (activeConversationId === nextId) {
      if (navKey !== lastThreadNavKeyRef.current) {
        lastThreadNavKeyRef.current = navKey;
        shouldAutoScrollRef.current = true;
        forceAutoScrollNextRef.current = false;
        pendingInitialPinToLatestRef.current = true;
        schedulePinToLatest("auto", nextId);
      }
      // Same thread already open: list refetches / socket can leave a stale unread on the active row; re-clear locally.
      clearUnreadLocal(nextId);
      return;
    }

    lastThreadNavKeyRef.current = navKey;
    shouldAutoScrollRef.current = true;
    pendingInitialPinToLatestRef.current = true;
    isSwitchingThreadRef.current = true;
    clearImagePreview();

    const convo = conversations.find((c) => c.id === nextId);
    activeConversationIdRef.current = nextId;
    setActiveConversationId(nextId);
    setActiveCounterpart(convo?.counterpart || null);
    setMessages([]);
    setNextCursor(null);
    pendingPrependScrollRef.current = null;
    suppressAutoScrollForPrependRef.current = false;
    loadingOlderRef.current = false;
    setLoadingOlder(false);
    clearUnreadLocal(nextId);

    (async () => {
      await loadInitialThread(nextId);
      if (activeConversationIdRef.current !== nextId) return;
      shouldAutoScrollRef.current = true;
      schedulePinToLatest("auto", nextId);
      const counterpartId = convo?.counterpart?.id ?? null;
      const joinRes = await socket.joinConversation(nextId).catch(() => null);
      await chatService.markRead(nextId).catch(() => {});
      try {
        const listData = await chatService.listConversations({ page: 1, limit: 50 });
        mergeServerConversationItemsIntoState(listData.items || []);
      } catch {
        /* keep local clearUnreadLocal if list refetch fails */
      }
      dispatchChatUnreadRefresh();
      if (
        counterpartId &&
        joinRes &&
        typeof joinRes.counterpartOnline === "boolean"
      ) {
        setOnlineByUserId((prev) => ({ ...prev, [counterpartId]: joinRes.counterpartOnline! }));
      }
    })();
    // loadInitialThread + socket.joinConversation are stable enough; avoid putting `socket` in deps (new object each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    conversationIdFromQuery,
    conversationIdsFingerprint,
    activeConversationId,
    sellerId,
    userRole,
    schedulePinToLatest,
    clearImagePreview,
    clearUnreadLocal,
    mergeServerConversationItemsIntoState,
  ]);

  // fetch signed URLs for attachments visible in loaded messages
  useEffect(() => {
    const keys = messages.flatMap((m) => (m.attachments || []) as string[]);
    if (keys.length > 0) ensureAttachmentUrls(keys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const handleSend = async () => {
    if (!activeConversationId) return;
    const text = newMessage.trim();
    if (!text) return;
    setNewMessage("");
    forceAutoScrollNextRef.current = true;

    // Optimistic UI update: add message immediately with temporary ID
    const tempId = `temp-${Date.now()}`;
    const optimisticMsg: ChatMessage = {
      id: tempId,
      conversationId: activeConversationId,
      senderId: "me",
      message: text,
      attachments: [],
      isRead: false,
      readAt: null,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [optimisticMsg, ...prev]);

    try {
      const sent = await socket.sendMessage({ conversationId: activeConversationId, text });
      setMessages((prev) => {
        const withoutOptimistic = prev.filter((m) => !String(m.id).startsWith("temp-"));
        if (withoutOptimistic.some((m) => m.id === sent.id)) return withoutOptimistic;
        return [sent, ...withoutOptimistic];
      });
    } catch (error) {
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      console.error("Failed to send message:", error);
    }
  };

  const handleFileSelect = (file: File) => {
    const allowedMime = new Set([
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/webp",
      "image/pjpeg",
    ]);
    const ext = file.name.split(".").pop()?.toLowerCase();
    const extOk = ext ? ["png", "jpg", "jpeg", "webp"].includes(ext) : false;
    const isValidType =
      (file.type && allowedMime.has(file.type)) || (!file.type && extOk);
    const isValidSize = file.size <= 10 * 1024 * 1024;

    if (!isValidType) {
      alert("Please select a PNG, JPG, or WEBP image");
      return;
    }
    if (!isValidSize) {
      alert("File size must be less than 10MB");
      return;
    }

    previewLoadSeqRef.current += 1;
    const seq = previewLoadSeqRef.current;
    setFullImagePreviewOpen(false);
    setPreviewFile(file);
    setDisplayPreviewSrc(null);

    const reader = new FileReader();
    reader.onload = () => {
      if (seq !== previewLoadSeqRef.current) return;
      const result = reader.result;
      if (typeof result === "string") setDisplayPreviewSrc(result);
    };
    reader.onerror = () => {
      if (seq !== previewLoadSeqRef.current) return;
      setPreviewFile(null);
      setDisplayPreviewSrc(null);
      alert("Could not load image preview. Try another file.");
    };
    reader.readAsDataURL(file);
  };

  const handleCancelPreview = () => {
    clearImagePreview();
  };

  const handleConfirmUpload = async () => {
    if (!previewFile || !activeConversationId) {
      console.warn("No file or conversation selected");
      return;
    }

    setUploading(true);
    forceAutoScrollNextRef.current = true;
    const tempId = `temp-${Date.now()}`;
    const caption = newMessage.trim();
    try {
      const uploaded = await chatService.uploadAttachment(previewFile);
      
      // Optimistic UI update for attachment message
      const optimisticMsg: ChatMessage = {
        id: tempId,
        conversationId: activeConversationId,
        senderId: "me",
        message: caption,
        attachments: [uploaded.key],
        isRead: false,
        readAt: null,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [optimisticMsg, ...prev]);

      // Fetch signed URL immediately for optimistic display (backend now allows recently uploaded files)
      try {
        const urlRes = await chatService.getAttachmentUrl(uploaded.key);
        setAttachmentUrls((prev) => ({ ...prev, [uploaded.key]: urlRes.url }));
      } catch (urlError) {
        console.warn("Failed to fetch preview URL, will retry when message arrives:", urlError);
        // Continue anyway - URL will be fetched when real message arrives
      }

      const sent = await socket.sendMessage({
        conversationId: activeConversationId,
        ...(caption ? { text: caption } : {}),
        attachments: [{ key: uploaded.key }],
      });
      setMessages((prev) => {
        const withoutOptimistic = prev.filter((m) => !String(m.id).startsWith("temp-"));
        if (withoutOptimistic.some((m) => m.id === sent.id)) return withoutOptimistic;
        return [sent, ...withoutOptimistic];
      });

      // Clear preview and caption (main input) after successful upload
      setNewMessage("");
      handleCancelPreview();
    } catch (error) {
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      console.error("Failed to send attachment:", error);
      alert("Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleImageButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    if (!fullImagePreviewOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullImagePreviewOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullImagePreviewOpen]);

  const goBackToInbox = useCallback(() => {
    setSearchParamsRef.current(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("conversationId");
        next.delete("sellerId");
        return next;
      },
      { replace: true }
    );
  }, []);

  /** Buyer: leave chat toward prior page (history / return path). Seller: collapse thread to inbox on compact layouts. */
  const handleChatHeaderBack = useCallback(() => {
    if (userRole !== "buyer") {
      goBackToInbox();
      return;
    }
    const from =
      typeof previousUrl === "string" &&
      previousUrl.startsWith("/") &&
      !previousUrl.startsWith("//")
        ? previousUrl
        : null;
    if (from) {
      navigate(from, { replace: true });
      return;
    }
    if (sellerId) {
      navigate(`/seller_detail/${sellerId}`, { replace: true });
      return;
    }
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/buyer/dashboard", { replace: true });
  }, [userRole, previousUrl, sellerId, navigate, goBackToInbox]);

  if (loading) {
    return (
      <div className="flex h-dvh w-full flex-col items-center justify-center gap-3 bg-slate-50/80">
        <Loader size={40} color="#f97316" />
        <p className="text-sm text-slate-500">Loading messages…</p>
      </div>
    );
  }

  return (
    <div className="h-dvh max-h-dvh flex flex-col overflow-hidden bg-slate-100/90">
      <header className="shrink-0 z-50 border-b border-slate-200/80 bg-white/95 backdrop-blur-md shadow-sm">
        <BizcoverIndiaFigmaHeader />
      </header>
      {/* <div>
        <Button
          onClick={() => {

            if (window.history.length > 1) {
              navigate(-1);
            } else if (sellerId) {
              navigate(`/seller_detail/${sellerId}`);
            } else {
              navigate("/home");
            }
          }}
        >
          BACK
        </Button>
      </div> */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden w-full max-w-[1600px] mx-auto px-2 sm:px-4 py-2 sm:py-4 lg:py-6">
        <div
          className={`flex-1 min-h-0 flex flex-col gap-2 sm:gap-3 lg:gap-4 overflow-hidden ${
            isCompactChat
              ? ""
              : "lg:grid lg:grid-cols-[minmax(280px,380px)_minmax(0,1fr)] lg:grid-rows-1 lg:items-stretch"
          }`}
        >
          {/* Inbox list */}
          <aside
            className={`flex flex-col min-h-0 rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden transition-[opacity,transform] duration-200 ease-out ${
              showListPanel ? "flex opacity-100" : "hidden lg:flex"
            } ${
              isCompactChat
                ? "flex-1 min-h-0 max-h-[85dvh] lg:max-h-none lg:h-full"
                : "lg:h-full lg:max-h-full"
            }`}
            aria-label="Conversations"
          >
            <div className="shrink-0 px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-white to-orange-50/40">
              <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Messages</h1>
              <p className="text-xs text-slate-500 mt-0.5">Select a conversation to continue</p>
              <div className="relative mt-3">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={conversationSearch}
                  onChange={(e) => setConversationSearch(e.target.value)}
                  placeholder="Search sellers"
                  className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 outline-none transition focus:border-orange-300 focus:ring-2 focus:ring-orange-100"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain p-2 sm:p-3 space-y-1.5">
              {conversations.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <p className="text-sm text-slate-500">No conversations yet.</p>
                  <p className="text-xs text-slate-400 mt-2">Start from a supplier or buyer profile.</p>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <p className="text-sm text-slate-500">No matching conversations.</p>
                  <p className="text-xs text-slate-400 mt-2">Try a different name or email.</p>
                </div>
              ) : (
                filteredConversations.map((c: any) => {
                  const isActive = c.id === activeConversationId;
                  const name = formatName(c.counterpart);
                  const initials = name
                    .split(" ")
                    .filter(Boolean)
                    .map((n: string) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase();
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`w-full text-left rounded-xl px-3 py-3 sm:py-3.5 transition-all duration-150 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2 ${
                        isActive
                          ? "bg-orange-50 shadow-sm ring-1 ring-orange-200/80"
                          : "hover:bg-slate-50 border border-transparent hover:border-slate-100"
                      }`}
                      onClick={() => {
                        clearUnreadLocal(c.id);
                        setSearchParams(
                          (prev) => {
                            const next = new URLSearchParams(prev);
                            next.set("conversationId", c.id);
                            next.delete("sellerId");
                            return next;
                          },
                          { replace: true }
                        );
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative shrink-0">
                          <Avatar className="h-11 w-11 sm:h-12 sm:w-12 ring-2 ring-white shadow-sm">
                            <AvatarImage src={c.counterpart?.profileImage || ""} />
                            <AvatarFallback className="text-sm font-medium bg-orange-100 text-orange-800">
                              {initials}
                            </AvatarFallback>
                          </Avatar>
                          {c.counterpart?.id && socket.isConnected && presenceReady ? (
                            <span
                              className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white ${
                                onlineByUserId[c.counterpart.id] ? "bg-emerald-500" : "bg-slate-300"
                              }`}
                              title={onlineByUserId[c.counterpart.id] ? "Online" : "Offline"}
                            />
                          ) : null}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-slate-900 text-sm sm:text-base truncate">
                              {name}
                            </span>
                            {!isActive && c.unreadCount > 0 ? (
                              <span className="shrink-0 min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center text-[11px] font-bold bg-orange-500 text-white rounded-full shadow-sm">
                                {c.unreadCount > 99 ? "99+" : c.unreadCount}
                              </span>
                            ) : null}
                          </div>
                          <p className="text-xs sm:text-sm text-slate-500 mt-0.5 leading-snug min-w-0 flex items-center gap-1.5">
                            {isPhotoOnlyConversationPreview(c.lastMessage) ? (
                              <>
                                <ImageIcon
                                  className="h-3.5 w-3.5 shrink-0 text-slate-500 opacity-90"
                                  aria-hidden
                                />
                                <span className="truncate">Photo</span>
                              </>
                            ) : (
                              <span className="truncate min-w-0">
                                {c.lastMessage?.message || "No messages yet"}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          {/* Thread: fixed column height; only the message list scrolls */}
          <section
            className={`flex flex-col min-h-0 rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden ${
              showThreadPanel
                ? "flex flex-1 min-h-0 lg:h-full lg:max-h-full"
                : "hidden lg:flex lg:h-full lg:max-h-full"
            }`}
            aria-label="Chat thread"
          >
            {/* <div className="shrink-0 flex items-center gap-2 px-2 sm:px-4 py-3 border-b border-slate-100 bg-white/90 backdrop-blur-sm">
              {isCompactChat ? (
                <button
                  type="button"
                  onClick={goBackToInbox}
                  className="shrink-0 flex items-center justify-center w-10 h-10 rounded-xl text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition-colors lg:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
                  aria-label="Back to inbox"
                >
                  <span className="text-xl leading-none" aria-hidden>
                    ‹
                  </span>
                </button>
              ) : null}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900 truncate">
                    {activeCounterpart ? formatName(activeCounterpart) : "Conversation"}
                  </h2>
                  {activeCounterpart?.id && socket.isConnected && presenceReady ? (
                    onlineByUserId[activeCounterpart.id] ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        Online
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                        Offline
                      </span>
                    )
                  ) : null}
                </div>
                <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5">
                  {socket.isConnected
                    ? presenceReady
                      ? "Messages are end-to-end on your session"
                      : "Syncing status…"
                    : "Connecting…"}
                </p>
              </div>
            </div> */}

<div className="shrink-0 flex items-center gap-2 px-2 sm:px-4 py-3 border-b border-slate-100 bg-white/90 backdrop-blur-sm">
  
  {/* Back Button */}
  {isCompactChat ? (
    <button
      type="button"
      onClick={handleChatHeaderBack}
      className="shrink-0 flex items-center justify-center w-10 h-10 rounded-xl text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition-colors lg:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
      aria-label="Back to inbox"
    >
      ‹
    </button>
  ) : null}

  {/* Avatar */}
  <div className="relative">
    {activeCounterpart?.profileImage ? (
      <img
        src={activeCounterpart.profileImage}
        alt="profile"
        className="w-10 h-10 rounded-full object-cover border border-slate-200 shadow-sm"
        onError={(e: any) => (e.target.style.display = "none")}
      />
    ) : (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center shadow-inner border border-white">
        
        {/* Stylish User Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5 text-slate-500"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12 12c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5zm0 2c-3.866 0-10 1.933-10 5.5V21h20v-1.5C22 15.933 15.866 14 12 14z" />
        </svg>
      </div>
    )}

    {/* Online Indicator */}
    {activeCounterpart?.id &&
      socket.isConnected &&
      presenceReady &&
      onlineByUserId[activeCounterpart.id] && (
        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full shadow" />
      )}
  </div>

  {/* Name + Status */}
  <div className="min-w-0 flex-1">
    <div className="flex items-center gap-2 flex-wrap">
      <h2 className="text-base sm:text-lg font-semibold text-slate-900 truncate">
        {activeCounterpart ? formatName(activeCounterpart) : "Conversation"}
      </h2>

      {activeCounterpart?.id && socket.isConnected && presenceReady ? (
        onlineByUserId[activeCounterpart.id] ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Online
          </span>
        ) : (
          <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
            Offline
          </span>
        )
      ) : null}
    </div>

    <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5">
      {socket.isConnected
        ? presenceReady
          ? "Messages are end-to-end on your session"
          : "Syncing status…"
        : "Connecting…"}
    </p>
  </div>
</div>

            <div className="flex-1 min-h-0 flex flex-col bg-slate-50/60 overflow-hidden">
              <div
                ref={chatContainerRef}
                onScroll={handleChatScroll}
                className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain [scrollbar-gutter:stable]"
                role="log"
                aria-live="polite"
                aria-relevant="additions"
              >
                <div className="w-full px-3 sm:px-5 lg:px-8 py-4 flex flex-col gap-3">
                {!messagesLoading && messages.length > 0 ? (
                  <div className="flex flex-col items-center shrink-0" aria-live="polite">
                    <div ref={loadOlderSentinelRef} className="h-1 w-full shrink-0" aria-hidden />
                    {loadingOlder ? (
                      <div className="flex items-center justify-center gap-2 py-2 text-xs text-slate-500">
                        <span className="h-3.5 w-3.5 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin shrink-0" />
                        Loading earlier messages…
                      </div>
                    ) : nextCursor ? (
                      <span className="sr-only">Scroll up to load earlier messages</span>
                    ) : (
                      <span className="text-[11px] text-slate-400 py-1.5">Beginning of conversation</span>
                    )}
                  </div>
                ) : null}
                {messagesLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <span className="h-4 w-4 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
                      Loading…
                    </div>
                  </div>
                ) : null}
                {!messagesLoading && messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                    <div
                      className="w-14 h-14 rounded-2xl border-2 border-dashed border-orange-200 bg-orange-50/50 mb-3"
                      aria-hidden
                    />
                    <p className="text-slate-600 font-medium">No messages yet</p>
                    <p className="text-sm text-slate-400 mt-1 max-w-xs">
                      Say hello — your message will appear here.
                    </p>
                  </div>
                ) : null}
                {(() => {
                  const chronological = [...messages].reverse();
                  return chronological.map((m: any, i: number) => {
                  const prev = i > 0 ? chronological[i - 1] : null;
                  const dayMs = localDayStartMs(m.createdAt);
                  const prevDayMs = prev ? localDayStartMs(prev.createdAt) : null;
                  const showDaySeparator =
                    dayMs != null && (prevDayMs == null || dayMs !== prevDayMs);
                  const dayLabel = showDaySeparator ? formatChatDayLabel(m.createdAt) : "";

                  const mine =
                    m.senderId === "me" ||
                    (activeCounterpart ? m.senderId !== activeCounterpart.id : false);
                  const attachmentKeys = (m.attachments || []) as string[];
                  const textTrimmed = String(m.message ?? "").trim();
                  const showMessageText =
                    textTrimmed.length > 0 &&
                    !(attachmentKeys.length > 0 && /^\[attachment\]$/i.test(textTrimmed));
                  return (
                    <React.Fragment key={m.id}>
                      {showDaySeparator && dayLabel ? (
                        <div
                          className="flex w-full shrink-0 items-center gap-3 py-2"
                          role="separator"
                          aria-label={dayLabel}
                        >
                          <div className="h-px flex-1 bg-slate-200/90" />
                          <span className="shrink-0 text-[11px] sm:text-xs font-semibold text-slate-500">
                            {dayLabel}
                          </span>
                          <div className="h-px flex-1 bg-slate-200/90" />
                        </div>
                      ) : null}
                    <div
                      className={`flex w-full shrink-0 ${mine ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[min(92%,36rem)] sm:max-w-[min(90%,42rem)] lg:max-w-[min(88%,52rem)] rounded-2xl px-3.5 py-2.5 sm:px-4 sm:py-3 text-sm shadow-sm ${
                          mine
                            ? "bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-br-md"
                            : "bg-white text-slate-900 border border-slate-200/80 rounded-bl-md"
                        }`}
                      >
                        {attachmentKeys.length > 0 ? (
                          <div className="space-y-2">
                            {(m.attachments || []).map((k: string) => {
                              const url = attachmentUrls[k];
                              return url ? (
                                <a
                                  key={k}
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="block rounded-lg overflow-hidden ring-1 ring-white/20"
                                >
                                  <img
                                    src={url}
                                    alt=""
                                    className="max-h-52 w-full object-cover sm:max-h-60 hover:opacity-95 transition-opacity"
                                    onLoad={() => {
                                      if (shouldAutoScrollRef.current) {
                                        scrollToLatestMessage("auto");
                                      }
                                    }}
                                  />
                                </a>
                              ) : (
                                <div
                                  key={k}
                                  className={`text-xs py-3 px-2 rounded-lg text-center ${
                                    mine ? "bg-white/10" : "bg-slate-100"
                                  }`}
                                >
                                  Loading image…
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                        {showMessageText ? (
                          <p
                            className={`whitespace-pre-wrap break-words leading-relaxed ${
                              attachmentKeys.length > 0 ? "mt-2" : ""
                            }`}
                          >
                            {m.message}
                          </p>
                        ) : null}
                        <div
                          className={`mt-1.5 flex items-center justify-end gap-1 text-[10px] sm:text-[11px] ${
                            mine ? "text-orange-100" : "text-slate-400"
                          }`}
                        >
                          <time dateTime={m.createdAt}>{formatMessageTime(m.createdAt)}</time>
                          {mine ? <OutgoingMessageTicks isRead={!!m.isRead} /> : null}
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                  );
                });
                })()}
                </div>
              </div>

              <div className="shrink-0 border-t border-slate-200/80 bg-white/95 backdrop-blur-md pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                <div className="w-full px-3 sm:px-5 lg:px-8 py-2 sm:py-3">
                {previewFile ? (
                  <div className="mb-2 sm:mb-3 p-2.5 sm:p-3 rounded-xl border border-orange-200 bg-orange-50/90 shadow-inner">
                    <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-3">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                      <div className="relative h-14 w-14 shrink-0 rounded-lg border border-orange-200 bg-white shadow-sm overflow-hidden flex items-center justify-center">
                        {displayPreviewSrc ? (
                          <img
                            src={displayPreviewSrc}
                            alt=""
                            className="h-full w-full object-cover"
                            decoding="async"
                          />
                        ) : (
                          <span className="text-[10px] text-slate-400 px-1 text-center leading-tight">
                            Loading…
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-800 truncate">{previewFile.name}</p>
                        <p className="text-[11px] text-slate-400 tabular-nums">
                          {(previewFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      </div>
                      <div className="flex items-center justify-end gap-1.5 shrink-0 sm:pl-0">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setFullImagePreviewOpen(true)}
                          disabled={!displayPreviewSrc || uploading}
                          className="h-8 px-2.5 text-xs gap-1"
                          aria-label="Preview image full screen"
                        >
                          <Eye className="size-3.5 shrink-0" aria-hidden />
                          Preview
                        </Button>
                        {/* <Button
                          variant="primary"
                          size="sm"
                          onClick={() => void handleConfirmUpload()}
                          disabled={uploading || !activeConversationId}
                          className="h-8 px-3 text-xs shrink-0"
                        >
                          {uploading ? "Sending…" : "Send"}
                        </Button> */}
                        <button
                          type="button"
                          onClick={handleCancelPreview}
                          disabled={uploading}
                          className="h-8 w-8 rounded-md border border-slate-200 bg-white text-slate-500 text-lg leading-none hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
                          aria-label="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="flex items-end gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => {
                      const input = e.currentTarget;
                      const f = input.files?.[0];
                      if (f) handleFileSelect(f);
                      requestAnimationFrame(() => {
                        input.value = "";
                      });
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleImageButtonClick}
                    disabled={uploading || !activeConversationId}
                    className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:border-slate-300 disabled:opacity-40 transition-colors flex items-center justify-center text-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
                    aria-label="Attach image"
                  >
                    <span className="text-sm font-semibold leading-none">+</span>
                  </button>
                  <label className="sr-only" htmlFor="chat-message-input">
                    Message
                  </label>
                  <textarea
                    id="chat-message-input"
                    value={newMessage}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setNewMessage(e.target.value)
                    }
                    placeholder={
                      previewFile ? "Add a caption (optional)…" : "Message…"
                    }
                    rows={1}
                    disabled={!activeConversationId}
                    className="flex-1 min-h-[44px] max-h-32 resize-none rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 shadow-inner focus:outline-none focus:ring-2 focus:ring-orange-400/40 focus:border-orange-300 disabled:bg-slate-50 disabled:text-slate-400"
                    onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (previewFile) {
                          if (!uploading && activeConversationId) void handleConfirmUpload();
                        } else {
                          handleSend();
                        }
                      }
                    }}
                  />
                  <Button
                    variant="primary"
                    onClick={() => {
                      if (previewFile) void handleConfirmUpload();
                      else handleSend();
                    }}
                    disabled={
                      !activeConversationId ||
                      uploading ||
                      (!previewFile && !newMessage.trim())
                    }
                    className="shrink-0 min-h-[44px] min-w-[4.5rem] px-4 rounded-xl shadow-md shadow-orange-500/20 disabled:shadow-none"
                  >
                    Send
                  </Button>
                </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {fullImagePreviewOpen && displayPreviewSrc ? (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setFullImagePreviewOpen(false)}
        >
          <button
            type="button"
            className="absolute top-3 right-3 z-10 h-10 w-10 rounded-full bg-white/10 text-white text-2xl leading-none hover:bg-white/20 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            onClick={(e) => {
              e.stopPropagation();
              setFullImagePreviewOpen(false);
            }}
            aria-label="Close preview"
          >
            ×
          </button>
          <img
            src={displayPreviewSrc}
            alt=""
            className="max-h-[min(88dvh,100%)] max-w-full w-auto object-contain rounded-lg shadow-2xl select-none"
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />
        </div>
      ) : null}
    </div>
  );
}

